import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { api } from '../../api.js';
import { Card, CardHeader, Badge, EmptyState, PageHeader, Spinner } from '../../components/ui.jsx';

export default function PermissionsSetup() {
  const { data: usersData, isLoading } = useQuery({ queryKey: ['users-list-perms'], queryFn: () => api.listUsers() });

  if (isLoading) return <div className="min-h-[40vh] grid place-items-center"><Spinner className="w-8 h-8 text-primary-600" /></div>;
  const users = usersData?.users || [];

  return (
    <>
      <PageHeader title="Permissions"
        description="Skillbase enforces role-based access automatically. Use this page to grant explicit overrides outside the standard hierarchy." />

      <Card className="mb-6 bg-gradient-to-br from-primary-50/40 to-accent-50/30">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-700 to-accent-600 grid place-items-center text-white shrink-0"><ShieldCheck className="w-4 h-4" /></div>
          <div>
            <h3 className="text-base font-semibold text-ink-900">How permissions work</h3>
            <ul className="mt-2 text-sm text-ink-700 space-y-1">
              <li>• <strong>Super-user</strong> sees everything in your company.</li>
              <li>• <strong>Group admin</strong> sees their assigned group(s) — both team and individual data.</li>
              <li>• <strong>Supervisor</strong> sees their direct reports' assessment results, not other team data.</li>
              <li>• <strong>Individual</strong> sees only their own data.</li>
              <li>• Explicit overrides (e.g. an HR partner who can see all leadership data across the company) live below.</li>
            </ul>
          </div>
        </div>
      </Card>

      {users.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No users yet" description="Invite users first, then manage their permissions here." />
      ) : (
        <Card>
          <CardHeader title="Roles by user" subtitle="Click a user to view full role + override details" />
          <div className="space-y-2">
            {users.map(u => (
              <UserRow key={u.id} user={u} />
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

function UserRow({ user }) {
  const { data } = useQuery({ queryKey: ['user-detail', user.id], queryFn: () => api.getUser(user.id) });
  const roles = data?.roles || [];
  const groups = data?.groups || [];
  return (
    <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-ink-50/60 border border-ink-100">
      <div className="flex-1">
        <div className="font-medium text-ink-900">{user.full_name}</div>
        <div className="text-xs text-ink-500">{user.email}</div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {roles.map(r => (
          <Badge key={r.id} variant={r.role === 'super_user' ? 'purple' : r.role === 'group_admin' ? 'blue' : r.role === 'supervisor' ? 'amber' : 'grey'}>
            {r.role.replace(/_/g, ' ')}
          </Badge>
        ))}
        {groups.length > 0 ? <Badge variant="grey">{groups.length} group{groups.length === 1 ? '' : 's'}</Badge> : null}
      </div>
    </div>
  );
}
